import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db } from '../firebase'

export interface Payment {
  id?: string
  studentId: string
  studentName: string
  amount: number
  currency: string
  method: 'Cash' | 'Zelle' | 'Venmo' | 'Check' | 'Other'
  paidAt: Timestamp | Date
  notes?: string
  createdAt?: Timestamp
  updatedAt?: Timestamp
  deleted: boolean
}

export interface PaymentFilters {
  studentId?: string
  fromDate?: Date
  toDate?: Date
}

/**
 * Add a new payment record
 */
export const addPayment = async (
  teacherId: string,
  paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt' | 'deleted'>,
): Promise<string> => {
  try {
    console.log('[PaymentService] Adding payment for teacher:', teacherId)

    const paymentsRef = collection(db, 'teachers', teacherId, 'payments')

    const newPayment = {
      ...paymentData,
      paidAt:
        paymentData.paidAt instanceof Date
          ? Timestamp.fromDate(paymentData.paidAt)
          : paymentData.paidAt,
      deleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(paymentsRef, newPayment)
    console.log('[PaymentService] Payment added successfully:', docRef.id)

    return docRef.id
  } catch (error) {
    console.error('[PaymentService] Error adding payment:', error)
    throw error
  }
}

/**
 * Update an existing payment record
 */
export const updatePayment = async (
  teacherId: string,
  paymentId: string,
  updates: Partial<Omit<Payment, 'id' | 'createdAt'>>,
): Promise<void> => {
  try {
    console.log('[PaymentService] Updating payment:', paymentId)

    const paymentRef = doc(db, 'teachers', teacherId, 'payments', paymentId)

    const updateData = {
      ...updates,
      paidAt:
        updates.paidAt instanceof Date
          ? Timestamp.fromDate(updates.paidAt)
          : updates.paidAt,
      updatedAt: serverTimestamp(),
    }

    await updateDoc(paymentRef, updateData)
    console.log('[PaymentService] Payment updated successfully')
  } catch (error) {
    console.error('[PaymentService] Error updating payment:', error)
    throw error
  }
}

/**
 * Soft delete a payment record
 */
export const deletePayment = async (
  teacherId: string,
  paymentId: string,
): Promise<void> => {
  try {
    console.log('[PaymentService] Soft deleting payment:', paymentId)

    const paymentRef = doc(db, 'teachers', teacherId, 'payments', paymentId)

    await updateDoc(paymentRef, {
      deleted: true,
      updatedAt: serverTimestamp(),
    })

    console.log('[PaymentService] Payment soft deleted successfully')
  } catch (error) {
    console.error('[PaymentService] Error deleting payment:', error)
    throw error
  }
}

/**
 * Get all payments for a teacher with optional filters
 */
export const getPayments = async (
  teacherId: string,
  filters?: PaymentFilters,
): Promise<Payment[]> => {
  try {
    console.log('[PaymentService] Fetching payments for teacher:', teacherId)

    const paymentsRef = collection(db, 'teachers', teacherId, 'payments')

    let q = query(
      paymentsRef,
      where('deleted', '==', false),
      orderBy('paidAt', 'desc'),
    )

    // Apply student filter if provided
    if (filters?.studentId && filters.studentId !== 'All') {
      q = query(
        paymentsRef,
        where('deleted', '==', false),
        where('studentId', '==', filters.studentId),
        orderBy('paidAt', 'desc'),
      )
    }

    const snapshot = await getDocs(q)

    let payments = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Payment[]

    // Apply date range filters client-side (Firestore doesn't support multiple range queries easily)
    if (filters?.fromDate || filters?.toDate) {
      payments = payments.filter((payment) => {
        const paidAt =
          payment.paidAt instanceof Timestamp
            ? payment.paidAt.toDate()
            : new Date(payment.paidAt)

        if (filters.fromDate && paidAt < filters.fromDate) return false
        if (filters.toDate && paidAt > filters.toDate) return false

        return true
      })
    }

    console.log('[PaymentService] Fetched payments:', payments.length)
    return payments
  } catch (error) {
    console.error('[PaymentService] Error fetching payments:', error)
    throw error
  }
}

/**
 * Calculate KPIs from payment data
 */
export const calculatePaymentKPIs = (
  payments: Payment[],
  dateRange: 'thisMonth' | 'last30Days' | 'yearToDate',
): {
  total: number
  count: number
} => {
  const now = new Date()
  let fromDate: Date

  switch (dateRange) {
    case 'thisMonth':
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1)
      break
    case 'last30Days':
      fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      break
    case 'yearToDate':
      fromDate = new Date(now.getFullYear(), 0, 1)
      break
  }

  const filteredPayments = payments.filter((payment) => {
    const paidAt =
      payment.paidAt instanceof Timestamp
        ? payment.paidAt.toDate()
        : new Date(payment.paidAt)
    return paidAt >= fromDate && paidAt <= now
  })

  const total = filteredPayments.reduce(
    (sum, payment) => sum + payment.amount,
    0,
  )

  return {
    total,
    count: filteredPayments.length,
  }
}
